import { useState } from "react";
import { Box, ButtonBase, ClickAwayListener, Typography } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTenantTranslation } from "hooks/formatters";
import { useVideoQuality } from "hooks/settings";
import { VIDEO_QUALITIES } from "hooks/hlsVideoQuality";
import type { VideoVariant } from "store/api/generatedApi";
import { useAppDispatch } from "src/store/store";
import { changeVideoQuality } from "store/slices/settings";
import { VideoOverlayIconButton } from "./VideoOverlayIconButton";

const CHECKMARK = "✓";

const QUALITY_LABEL_KEYS = {
  original: "high",
  low: "low",
} as const satisfies Record<VideoVariant, string>;

export const VideoQualityMenu = () => {
  const { t } = useTenantTranslation();
  const quality = useVideoQuality();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsOpen(false);
      }}
    >
      <Box sx={{ position: "relative" }}>
        {isOpen ? (
          <Box
            role="menu"
            sx={{
              position: "absolute",
              left: 0,
              top: "calc(100% + 8px)",
              minWidth: 180,
              py: 1,
              borderRadius: 2,
              backgroundColor: "rgba(28, 28, 28, 0.9)",
              color: "common.white",
            }}
          >
            <Typography
              variant="body2"
              sx={{ px: 2, pb: 0.5, color: "grey.400" }}
            >
              {t("quality")}
            </Typography>
            {VIDEO_QUALITIES.map((option) => (
              <ButtonBase
                key={option}
                role="menuitemradio"
                aria-checked={option === quality}
                onClick={() => {
                  dispatch(changeVideoQuality(option));
                  setIsOpen(false);
                }}
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "flex-start",
                  gap: 1,
                  px: 2,
                  py: 1,
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                }}
              >
                <Box component="span" sx={{ width: 16 }}>
                  {option === quality ? CHECKMARK : null}
                </Box>
                <Typography variant="body2">
                  {t(QUALITY_LABEL_KEYS[option])}
                </Typography>
              </ButtonBase>
            ))}
          </Box>
        ) : null}
        <VideoOverlayIconButton
          aria-label={t("settings")}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
        >
          <SettingsIcon />
        </VideoOverlayIconButton>
      </Box>
    </ClickAwayListener>
  );
};
